/**
 * The skinned raider — Kenney's CC0 "Animated Characters" rig, driven by the sim.
 *
 * Three rules shape this file:
 *
 *  1. **It must never be on the critical path.** The GLB is ~104 KB and the game is playable
 *     without it, so nothing awaits it. `createFigure()` hands back a working figure immediately
 *     (the procedural `ToyFigure`) and upgrades it in place when the asset lands. If the download
 *     fails, the game simply stays procedural — no error screen, no missing player.
 *  2. **The clips are a vocabulary, not a state machine.** The sim owns pose; this file only maps
 *     `RaiderPose` onto three clips and scales playback by real speed, so a slow walk and a sprint
 *     are the same clip at different rates.
 *  3. **`flung` stays rigid.** Being launched across the room is the signature joke, and a
 *     tumbling one-piece toy reads funnier — and never wedges — compared to a ragdoll. So a fling
 *     freezes the skeleton in a splayed pose and rotates the *whole* figure by the sim's `tumble`
 *     accumulator, exactly as the procedural figure did.
 *
 * Asset: kenney.nl "Animated Characters: Protagonists" (CC0). Built by tools/build-character.sh —
 * see THIRD_PARTY.md. The GLB carries no materials on purpose; the toon material and the skin
 * texture are assembled here.
 */

import * as THREE from 'three'

import { seedFromString } from '../core/rng'
import { SIM } from '../sim/config'
import type { RaiderState } from '../sim/types'
import { chestGeometry } from './geometry'
import { GHOST_PALETTE, RAIDER_PALETTE, ToyFigure, type Figure, type FigurePalette } from './figure'
import { toonGradient } from './toon'

const GLB_URL = 'character.glb'

/**
 * Bind-pose height of the source asset, in its own units — printed by
 * tools/build-character.py as `TR-CHAR HEIGHT`. Kept as a constant rather than measured at load
 * time so the figure's size can never depend on which clip happened to be playing.
 */
const CHARACTER_HEIGHT = 3.76469

/** How tall the raider stands in world units. Matches the old procedural figure. */
const TARGET_HEIGHT = 1.0

/** Kenney ships one rig with four looks. Which one you get is your name, hashed. */
export const SKINS = ['skaterMaleA', 'skaterFemaleA', 'criminalMaleA', 'cyborgFemaleA'] as const
export type SkinName = (typeof SKINS)[number]

export function skinForName(name: string): SkinName {
  const i = seedFromString(name || 'stranger') % SKINS.length
  return SKINS[i] as SkinName
}

interface CharacterAsset {
  scene: THREE.Group
  clips: Map<string, THREE.AnimationClip>
}

let assetPromise: Promise<CharacterAsset | null> | null = null

/**
 * Start fetching the character. Safe to call repeatedly and safe to ignore the result — the point
 * of calling it early (from the menu) is that the download overlaps with the player reading.
 */
export function preloadCharacter(): Promise<CharacterAsset | null> {
  if (assetPromise) return assetPromise
  assetPromise = (async () => {
    try {
      // Dynamic so GLTFLoader lands in its own chunk instead of the first paint.
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
      const gltf = await new GLTFLoader().loadAsync(GLB_URL)
      const clips = new Map<string, THREE.AnimationClip>()
      for (const clip of gltf.animations) clips.set(clip.name, clip)
      if (!clips.has('idle') || !clips.has('run') || !clips.has('jump')) {
        // A GLB missing a clip would animate wrongly rather than visibly fail, so refuse it.
        console.warn('[toyraiders] character.glb is missing clips:', [...clips.keys()])
        return null
      }
      return { scene: gltf.scene, clips }
    } catch (err) {
      console.warn('[toyraiders] character asset unavailable, staying procedural:', err)
      return null
    }
  })()
  return assetPromise
}

const textures = new Map<string, THREE.Texture>()

function skinTexture(skin: SkinName): THREE.Texture {
  const hit = textures.get(skin)
  if (hit) return hit
  const tex = new THREE.TextureLoader().load(`skins/${skin}.webp`)
  tex.colorSpace = THREE.SRGBColorSpace
  // glTF UVs put the origin at top-left; TextureLoader defaults to flipY:true, which silently
  // swaps rows of the skin atlas and puts the t-shirt on the character's face.
  tex.flipY = false
  tex.anisotropy = 4
  textures.set(skin, tex)
  return tex
}

/** Poses that hold a single frame instead of playing. */
const FROZEN: Partial<Record<RaiderState['pose'], number>> = { flung: 0.18 }

class SkinnedFigure implements Figure {
  readonly root = new THREE.Group()
  /** Everything that tumbles as one piece when flung. */
  private tilt = new THREE.Group()
  private body = new THREE.Group()
  private mixer: THREE.AnimationMixer
  private actions = new Map<string, THREE.AnimationAction>()
  private current = ''
  private held: THREE.Group
  private material: THREE.MeshToonMaterial
  private disposables: Array<{ dispose(): void }> = []

  constructor(asset: CharacterAsset, skin: SkinName, opts: { ghost?: boolean } = {}) {
    // SkeletonUtils.clone (not Object3D.clone) because a skinned mesh needs its own skeleton;
    // sharing one would make the replay ghost and the live raider pose each other.
    const model = cloneSkinned(asset.scene)
    model.scale.setScalar(TARGET_HEIGHT / CHARACTER_HEIGHT)

    this.material = new THREE.MeshToonMaterial({
      map: skinTexture(skin),
      gradientMap: toonGradient(),
    })
    if (opts.ghost) {
      this.material.transparent = true
      this.material.opacity = 0.5
      this.material.depthWrite = false
      // Drain the colour so a replay reads as a recording, not a second live player.
      this.material.color = new THREE.Color(0x9fb0c4)
    }
    this.disposables.push(this.material)

    model.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.material = this.material
        o.castShadow = !opts.ghost
        o.receiveShadow = false
        o.frustumCulled = false // one skinned mesh; its bounds move more than the box says
      }
    })

    this.body.add(model)
    this.tilt.add(this.body)
    this.root.add(this.tilt)

    // The loot, hugged to the chest.
    //
    // Deliberately parented to the yawing body group rather than to the `Spine` bone: bone space
    // here is the source asset's axes *and* its scale, so a bone-parented prop needs an
    // orientation nobody can predict from reading the code (it first came out floating behind the
    // raider's head). Body space is metres, +Z is forward, and the small torso bob it gives up is
    // invisible at this size. Carrying has to be legible three seconds into a shared clip; that is
    // the only requirement.
    const chest = chestGeometry()
    this.disposables.push(chest.body, chest.accent)
    const gold = new THREE.MeshToonMaterial({ color: 0xb98a52, gradientMap: toonGradient() })
    const trim = new THREE.MeshToonMaterial({ color: 0xffd166, gradientMap: toonGradient() })
    this.disposables.push(gold, trim)
    this.held = new THREE.Group()
    this.held.add(new THREE.Mesh(chest.body, gold), new THREE.Mesh(chest.accent, trim))
    this.held.visible = false
    this.held.scale.setScalar(0.55)
    this.held.position.set(0, TARGET_HEIGHT * 0.34, TARGET_HEIGHT * 0.3)
    this.body.add(this.held)

    this.mixer = new THREE.AnimationMixer(model)
    for (const [name, clip] of asset.clips) {
      const action = this.mixer.clipAction(clip)
      action.enabled = true
      this.actions.set(name, action)
    }
    this.play('idle', 1)
  }

  private play(name: string, timeScale: number, frozenAt?: number): void {
    const next = this.actions.get(name)
    if (!next) return
    next.timeScale = timeScale
    if (this.current !== name) {
      const prev = this.actions.get(this.current)
      next.reset().play()
      // Cross-fade is short on purpose: the sim flips pose the instant physics does, and a slow
      // blend would show the raider still strolling a beat after a spring hit them.
      if (prev && prev !== next) next.crossFadeFrom(prev, 0.12, true)
      this.current = name
    }
    if (frozenAt !== undefined) {
      next.paused = true
      next.time = frozenAt * next.getClip().duration
    } else {
      next.paused = false
    }
  }

  update(state: RaiderState, dt: number): void {
    this.root.position.set(state.x, state.y - SIM.raider.standY, state.z)
    this.held.visible = state.carrying

    switch (state.pose) {
      case 'run': {
        // One clip covers every ground speed; the rate carries the difference. The divisor is the
        // speed the cycle was authored to look right at.
        this.play('run', Math.max(0.45, state.speed / 3.4))
        break
      }
      case 'air':
        this.play('jump', 1)
        break
      case 'stuck':
        // Same run cycle, wading. Reads as effort that isn't going anywhere.
        this.play('run', 0.35)
        break
      case 'flung':
        this.play('jump', 0, FROZEN.flung)
        break
      default:
        this.play('idle', 1)
        break
    }

    if (state.pose === 'flung') {
      const travel = Math.atan2(state.vx, state.vz)
      this.tilt.rotation.set(state.tumble, travel, 0, 'YXZ')
      this.tilt.position.y = TARGET_HEIGHT * 0.36
      this.body.rotation.y = 0
    } else {
      this.tilt.rotation.set(0, 0, 0)
      this.tilt.position.y = 0
      this.body.rotation.y = state.yaw
    }

    this.mixer.update(dt)
  }

  dispose(): void {
    this.mixer.stopAllAction()
    this.mixer.uncacheRoot(this.mixer.getRoot())
    for (const d of this.disposables) d.dispose()
    this.disposables = []
    this.root.removeFromParent()
  }
}

let skeletonUtils: typeof import('three/examples/jsm/utils/SkeletonUtils.js') | null = null

function cloneSkinned(source: THREE.Group): THREE.Group {
  if (!skeletonUtils) throw new Error('SkeletonUtils not loaded')
  return skeletonUtils.clone(source) as THREE.Group
}

/**
 * A figure that works now and gets better shortly.
 *
 * Callers get a real object synchronously — no nullable figure, no "loading" branch in the game
 * modes. Internally it is the procedural toy until the GLB resolves, then the skinned character
 * takes over mid-frame. The swap is invisible because both are driven by the same `RaiderState`.
 */
class UpgradingFigure implements Figure {
  readonly root = new THREE.Group()
  private inner: Figure
  private disposed = false

  constructor(palette: FigurePalette, opts: { ghost?: boolean; name?: string } = {}) {
    this.inner = new ToyFigure(palette, opts)
    this.root.add(this.inner.root)
    void this.upgrade(opts)
  }

  private async upgrade(opts: { ghost?: boolean; name?: string }): Promise<void> {
    const asset = await preloadCharacter()
    if (!asset || this.disposed) return
    if (!skeletonUtils) {
      skeletonUtils = await import('three/examples/jsm/utils/SkeletonUtils.js')
    }
    if (this.disposed) return
    const skinned = new SkinnedFigure(asset, skinForName(opts.name ?? ''), opts)
    this.inner.dispose()
    this.inner = skinned
    this.root.add(skinned.root)
  }

  update(state: RaiderState, dt: number): void {
    this.inner.update(state, dt)
  }

  dispose(): void {
    this.disposed = true
    this.inner.dispose()
    this.root.removeFromParent()
  }
}

export function createFigure(
  palette: FigurePalette = RAIDER_PALETTE,
  opts: { ghost?: boolean; name?: string } = {},
): Figure {
  return new UpgradingFigure(palette, opts)
}

export { GHOST_PALETTE, RAIDER_PALETTE }
